import { useCallback, useEffect, useRef } from "react"
import type { ReactNode } from "react"
import { useT } from "../i18n/index.tsx"
import { IconButton } from "./Button.tsx"

/**
 * 옆에서 밀려 들어오는 판. 목록을 떠나지 않고 한 건을 들여다볼 때 쓴다.
 *
 * Esc 나 바깥 클릭으로 닫히고, 열려 있는 동안 뒤 목록이 같이 스크롤되지 않게 몸통을 잠근다.
 * Tab 은 판 안에서만 돈다 — 모달이므로 뒤 목록으로 새면 안 된다.
 */
const FOCUSABLE =
  "a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), " +
  'textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])'

export function Drawer({
  title,
  onClose,
  side = "right",
  width = "half",
  footer,
  children,
}: {
  title: ReactNode
  onClose: () => void
  /** right = 상세 옆판(기본), left = 모바일 메뉴. 좁은 화면에서는 둘 다 전체 폭. */
  side?: "right" | "left"
  width?: "half" | "narrow"
  footer?: ReactNode
  children: ReactNode
}) {
  const t = useT()
  const panelRef = useRef<HTMLDivElement>(null)
  const restoreFocusTo = useRef<Element | null>(null)

  const focusables = useCallback(
    () =>
      [...(panelRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? [])].filter(
        (element) => element.offsetParent !== null,
      ),
    [],
  )

  useEffect(() => {
    restoreFocusTo.current = document.activeElement

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose()
        return
      }
      if (event.key !== "Tab") return

      const targets = focusables()
      if (targets.length === 0) return
      const first = targets[0]
      const last = targets[targets.length - 1]

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }
    document.addEventListener("keydown", onKeyDown)

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = "hidden"

    // 열자마자 판 안으로 포커스를 옮겨야 Tab 이 뒤 목록으로 새지 않는다.
    const targets = focusables()
    if (targets.length > 0) targets[0].focus()
    else panelRef.current?.focus()

    return () => {
      document.removeEventListener("keydown", onKeyDown)
      document.body.style.overflow = previousOverflow
      if (restoreFocusTo.current instanceof HTMLElement) restoreFocusTo.current.focus()
    }
  }, [onClose, focusables])

  return (
    <div className={`drawer-overlay drawer-overlay--${side}`} onMouseDown={onClose}>
      <div
        className={`drawer drawer--${side} drawer--${width}`}
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === "string" ? title : undefined}
        tabIndex={-1}
        ref={panelRef}
        // 판 안쪽 클릭이 바깥 클릭으로 새어 나가 창이 닫히면 안 된다.
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="drawer__head">
          <div className="drawer__title">{title}</div>
          <IconButton icon="close" label={t("common.close")} size="sm" onClick={onClose} />
        </div>
        <div className="drawer__body">{children}</div>
        {footer ? <div className="drawer__foot">{footer}</div> : null}
      </div>
    </div>
  )
}
